/**
 * Helpers for building per-page metadata consumed by vue-meta.
 *
 * Each page component exposes a `metaInfo` (object or function) built with
 * buildMeta() so every route gets its own <title>, description, canonical URL,
 * and Open Graph / Twitter Card tags.
 */

export const SITE_NAME = 'لایوی';
export const SITE_URL = (process.env.VUE_APP_SITE_URL || 'https://livey.ir').replace(/\/$/, '');

const DEFAULT_IMAGE = `${SITE_URL}/images/icons/web-app-manifest-512x512.png`;

/** Strips HTML tags and collapses whitespace — used for description from rich text. */
export function stripHtml(html) {
    if (!html) return '';
    return String(html)
        .replace(/<[^>]*>/g, ' ')
        .replace(/&nbsp;/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}

export function truncate(text, max = 160) {
    if (!text) return '';
    return text.length > max ? `${text.slice(0, max - 1).trim()}…` : text;
}

/**
 * Builds a vue-meta metaInfo object.
 * @param {Object} opts
 * @param {string} opts.title    Full page title (used verbatim).
 * @param {string} opts.description
 * @param {string} [opts.path]   Route path, e.g. "/blog". Used for canonical + og:url.
 * @param {string} [opts.image]  Absolute or root-relative image URL.
 * @param {string} [opts.type]   Open Graph type ("website" | "article").
 */
export function buildMeta({ title, description, path = '', image, type = 'website' }) {
    const url = `${SITE_URL}${path}`;
    const img = image
        ? (image.startsWith('http') ? image : `${SITE_URL}${image}`)
        : DEFAULT_IMAGE;

    return {
        title,
        meta: [
            { vmid: 'description', name: 'description', content: description },

            // Open Graph
            { vmid: 'og:title', property: 'og:title', content: title },
            { vmid: 'og:description', property: 'og:description', content: description },
            { vmid: 'og:type', property: 'og:type', content: type },
            { vmid: 'og:url', property: 'og:url', content: url },
            { vmid: 'og:image', property: 'og:image', content: img },
            { vmid: 'og:site_name', property: 'og:site_name', content: SITE_NAME },
            { vmid: 'og:locale', property: 'og:locale', content: 'fa_IR' },

            // Twitter
            { vmid: 'twitter:card', name: 'twitter:card', content: 'summary_large_image' },
            { vmid: 'twitter:title', name: 'twitter:title', content: title },
            { vmid: 'twitter:description', name: 'twitter:description', content: description },
            { vmid: 'twitter:image', name: 'twitter:image', content: img },
        ],
        link: [
            { vmid: 'canonical', rel: 'canonical', href: url },
        ],
    };
}
